// src/services/attendance.service.js
import { api } from './api';

class AttendanceService {
    // ============================================================
    // NEW: Multiple Punch In/Out Support
    // ============================================================

    /**
     * Record a punch (check-in or check-out)
     * Supports multiple punches per day
     * @param {string} punchType - 'check_in' or 'check_out'
     * @param {object} location - Location data (optional)
     * @param {string} notes - Punch notes (optional)
     * @param {number|string} shiftId - Employee's assigned shift ID (optional, helps with overnight shift date assignment)
     */
    async punch(punchType, location = null, notes = null, shiftId = null) {
        try {
            const payload = {
                punchType, // 'check_in' or 'check_out'
                location: location || { type: 'Office' },
                notes
            };

            // Include shift_id to help backend determine correct attendance_date for overnight shifts
            if (shiftId) {
                payload.shift_id = shiftId;
            }

            const response = await api.post('/api/attendance/punch', payload);
            return response.data;
        } catch (error) {
            console.error('Error recording punch:', error);
            throw error;
        }
    }

    /**
     * Get today's punch records
     */
    async getTodayPunches() {
        try {
            const response = await api.get('/api/attendance/punches/today');
            return response.data;
        } catch (error) {
            console.error('Error fetching today\'s punches:', error);
            throw error;
        }
    }

    // ============================================================
    // LEGACY: Keep old methods for backward compatibility
    // ============================================================

    /**
     * Check in (legacy - redirects to punch)
     */
    async checkIn(location = null) {
        return this.punch('check_in', location);
    }

    /**
     * Check out (legacy - redirects to punch)
     */
    async checkOut(location = null) {
        return this.punch('check_out', location);
    }

    /**
     * Get my attendance records
     */
    async getMyAttendance(month, year) {
        try {
            const params = {};
            if (month) params.month = month;
            if (year) params.year = year;

            const response = await api.get('/api/attendance/my-attendance', { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching attendance:', error);
            // Fallback to localStorage
            return this.getLocalStorageAttendance();
        }
    }

    /**
     * Get team attendance (for managers)
     */
    async getTeamAttendance(date) {
        try {
            const params = date ? { date } : {};
            const response = await api.get('/api/attendance/team', { params });
            return {
                success: true,
                data: response.data.attendance || [],
                message: 'Team attendance fetched successfully'
            };
        } catch (error) {
            console.error('Error fetching team attendance:', error);
            return {
                success: false,
                data: [],
                error: this._extractErrorMessage(error),
                message: 'Failed to fetch team attendance'
            };
        }
    }

    /**
     * Get all attendance (for admins)
     */
    async getAllAttendance(date, department) {
        try {
            const params = {};
            if (date) params.date = date;
            if (department) params.department = department;

            const response = await api.get('/api/admin/attendance', { params });
            return {
                success: true,
                data: response.data.attendance || [],
                message: 'Attendance fetched successfully'
            };
        } catch (error) {
            console.error('Error fetching all attendance:', error);
            return {
                success: false,
                data: [],
                error: this._extractErrorMessage(error),
                message: 'Failed to fetch attendance'
            };
        }
    }

    /**
     * Get employee attendance (for admin/payroll)
     * Fetches attendance for a specific employee
     * Returns same format as getMyAttendance for consistency
     */
    async getEmployeeAttendance(employeeId, month, year) {
        try {
            const params = {};
            if (month) params.month = month;
            if (year) params.year = year;

            const response = await api.get(`/api/attendance/employee/${employeeId}`, { params });
            // Return response.data directly (same format as getMyAttendance)
            return response.data;
        } catch (error) {
            console.error(`Error fetching attendance for employee ${employeeId}:`, error);
            // Return empty structure matching getMyAttendance format
            return { monthly: [], stats: null, today: null };
        }
    }

    // ============================================================
    // NEW: Attendance Regularization Requests
    // ============================================================

    /**
     * Submit regularization request
     */
    async submitRegularizationRequest(data) {
        try {
            const response = await api.post('/api/attendance/regularization/submit', data);
            return {
                success: true,
                data: response.data,
                message: 'Regularization request submitted successfully'
            };
        } catch (error) {
            console.error('Error submitting regularization request:', error);
            return {
                success: false,
                data: null,
                error: this._extractErrorMessage(error),
                message: 'Failed to submit regularization request'
            };
        }
    }

    /**
     * Get my regularization requests
     */
    async getMyRegularizationRequests() {
        try {
            const response = await api.get('/api/attendance/regularization/my-requests');
            return {
                success: true,
                data: response.data,
                message: 'Regularization requests fetched successfully'
            };
        } catch (error) {
            console.error('Error fetching regularization requests:', error);
            return {
                success: false,
                data: { requests: [] },
                error: this._extractErrorMessage(error),
                message: 'Failed to fetch regularization requests'
            };
        }
    }

    /**
     * Get team regularization requests (for managers)
     */
    async getTeamRegularizationRequests() {
        try {
            const response = await api.get('/api/attendance/regularization/team-requests');
            return {
                success: true,
                data: response.data,
                message: 'Team regularization requests fetched successfully'
            };
        } catch (error) {
            console.error('Error fetching team regularization requests:', error);
            return {
                success: false,
                data: { requests: [] },
                error: this._extractErrorMessage(error),
                message: 'Failed to fetch team regularization requests'
            };
        }
    }

    /**
     * Process regularization request (approve/reject) - for managers
     */
    async processRegularizationRequest(requestId, action, comments = null, decisionType = null) {
        try {
            const response = await api.post('/api/attendance/regularization/process', {
                requestId,
                action, // 'approve' or 'reject'
                comments,
                decision_type: decisionType // 'regular', 'comp_off', 'overtime'
            });
            return response.data;
        } catch (error) {
            console.error('Error processing regularization request:', error);
            throw error;
        }
    }

    /**
     * Check regularization context (non-working day? overtime?)
     */
    async checkRegularizationContext(employeeId, attendanceDate, requestedPunchIn, requestedPunchOut) {
        try {
            const params = {
                employeeId,
                attendanceDate,
                requestedPunchIn,
                requestedPunchOut
            };
            const response = await api.get('/api/attendance/regularization/check-context', { params });
            return response.data;
        } catch (error) {
            console.error('Error checking regularization context:', error);
            throw error;
        }
    }

    /**
     * Regularize attendance (legacy - redirects to new method)
     */
    async regularizeAttendance(data) {
        return this.submitRegularizationRequest({
            attendanceDate: data.date,
            regularizationType: 'correction',
            requestedPunchIn: data.checkInTime,
            requestedPunchOut: data.checkOutTime,
            reason: data.reason,
            managerId: data.managerId
        });
    }

    /**
     * Fallback: Get attendance from localStorage
     */
    /**
     * Submit work from home request
     */
    async requestWorkFromHome(data) {
        try {
            const response = await api.post('/api/attendance/wfh/request', data);
            return {
                success: true,
                data: response.data,
                message: 'Work from home request submitted successfully'
            };
        } catch (error) {
            console.error('Error submitting WFH request:', error);
            return {
                success: false,
                data: null,
                error: this._extractErrorMessage(error),
                message: 'Failed to submit work from home request'
            };
        }
    }

    /**
     * Get my work from home requests
     */
    async getMyWfhRequests() {
        try {
            const response = await api.get('/api/attendance/wfh/my-requests');
            return response.data;
        } catch (error) {
            console.error('Error fetching WFH requests:', error);
            throw error;
        }
    }

    /**
     * Get team work from home requests (for managers)
     */
    async getTeamWfhRequests() {
        try {
            const response = await api.get('/api/attendance/wfh/team-requests');
            return response.data;
        } catch (error) {
            console.error('Error fetching team WFH requests:', error);
            throw error;
        }
    }

    /**
     * Get all work from home requests (for admins and managers)
     * Backend automatically returns ALL requests for admin users
     * and only team requests for managers based on role
     */
    async getAllWfhRequests() {
        try {
            const response = await api.get('/api/attendance/wfh/team-requests');
            return response.data;
        } catch (error) {
            console.error('Error fetching all WFH requests:', error);
            throw error;
        }
    }

    /**
     * Process work from home request (approve/reject) - for managers
     */
    async processWfhRequest(requestId, action, comments = null) {
        try {
            const response = await api.post('/api/attendance/wfh/process', {
                requestId,
                action, // 'approve' or 'reject'
                comments
            });
            return response.data;
        } catch (error) {
            console.error('Error processing WFH request:', error);
            throw error;
        }
    }

    /**
     * Revoke/delete an attendance record (admin only)
     * Removes the attendance entry for a specific day
     */
    async revokeAttendance(attendanceId, reason = '') {
        try {
            const response = await api.delete(`/admin/attendance/${attendanceId}/revoke`, {
                data: { reason }
            });
            return response.data;
        } catch (error) {
            console.error('Error revoking attendance:', error);
            throw error;
        }
    }

    getLocalStorageAttendance() {
        // try {
        //     const stored = localStorage.getItem('hrportal_attendance');
        //     if (!stored) {
        //         return { today: null, monthly: [], stats: {} };
        //     }
        //     return JSON.parse(stored);
        // } catch (error) {
        //     console.error('Error loading attendance from localStorage:', error);
        //     return { today: null, monthly: [], stats: {} };
        // }
    }
  /**
     * Helper method to extract error messages from API responses
     */
    _extractErrorMessage(error) {
        if (error.response?.data?.error) {
            return error.response.data.error;
        }
        if (error.response?.data?.message) {
            return error.response.data.message;
        }
        if (error.message) {
            return error.message;
        }
        return 'An unexpected error occurred';
    }

    /**
     * Get attendance records for analytics dashboard
     */
    async getAttendanceAnalytics(filters = {}) {
        try {
            const params = new URLSearchParams();
            Object.entries(filters).forEach(([key, value]) => {
                if (value !== undefined && value !== null) {
                    params.append(key, value);
                }
            });

            const response = await api.get(`/api/attendance/analytics?${params}`);
            return {
                success: true,
                data: response.data,
                message: 'Attendance analytics fetched successfully'
            };
        } catch (error) {
            console.error('Error fetching attendance analytics:', error);
            return {
                success: false,
                data: {
                    summary: { present: 0, absent: 0, leave: 0, total: 0 },
                    trends: [],
                    departmentWise: []
                },
                error: this._extractErrorMessage(error),
                message: 'Failed to fetch attendance analytics'
            };
        }
    }

    /**
     * Get attendance summary for dashboard widgets
     */
    async getAttendanceSummary(date = null) {
        try {
            const params = date ? { date } : {};
            const response = await api.get('/api/attendance/summary', { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching attendance summary:', error);
            return { presentDays: 0, totalWorkingDays: 0, totalDaysInMonth: 0 };
        }
    }

    /**
     * Calculate working days based on user's weekly off and holiday plan
     * WD = TD - ND - HD
     */
    async calculateWorkingDays(year, month, userProfile, holidayPlanId) {
        try {
            // Import services
            const { default: employeeService } = await import('./employee.service');
            const { default: holidayPlanService } = await import('./holidayPlan.service');

            // Total days in month (TD)
            const totalDaysInMonth = new Date(year, month, 0).getDate();

            // Get user's weekly off configuration
            let weeklyOffDays = [];
            const weeklyOff = userProfile?.weekly_off || userProfile?.weeklyOff;

            if (weeklyOff) {
                // Parse weekly off - could be "Sunday", "Sundays", "Saturday,Sunday", "Saturday & Sunday", etc.
                const weeklyOffStr = weeklyOff.toLowerCase();
                const dayMap = {
                    'sunday': 0,
                    'sundays': 0,
                    'monday': 1,
                    'mondays': 1,
                    'tuesday': 2,
                    'tuesdays': 2,
                    'wednesday': 3,
                    'wednesdays': 3,
                    'thursday': 4,
                    'thursdays': 4,
                    'friday': 5,
                    'fridays': 5,
                    'saturday': 6,
                    'saturdays': 6
                };

                // Split by comma, ampersand, or "and" - handle multiple formats
                weeklyOffDays = weeklyOffStr
                    .split(/[,&]|and/)  // Split by comma, &, or "and"
                    .map(day => day.trim())  // Trim whitespace
                    .map(day => dayMap[day])  // Map to day numbers
                    .filter(day => day !== undefined);  // Filter out invalid days

                console.log('🗓️ Parsed Weekly Off Days:', {
                    original: weeklyOff,
                    lowercased: weeklyOffStr,
                    splitParts: weeklyOffStr.split(/[,&]|and/).map(d => d.trim()),
                    parsed: weeklyOffDays,
                    dayNames: weeklyOffDays.map(d => Object.keys(dayMap).find(k => dayMap[k] === d && !k.endsWith('s')))
                });
            } else {
                // Default to Sunday if not configured
                weeklyOffDays = [0];
                console.log('🗓️ Using default weekly off: Sunday');
            }

            // Calculate non-working days (ND) - based on weekly offs
            let nonWorkingDays = 0;
            const weeklyOffBreakdown = {};

            for (let day = 1; day <= totalDaysInMonth; day++) {
                const date = new Date(year, month - 1, day);
                const dayOfWeek = date.getDay();
                if (weeklyOffDays.includes(dayOfWeek)) {
                    nonWorkingDays++;
                    // Track each day type
                    const dayName = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][dayOfWeek];
                    weeklyOffBreakdown[dayName] = (weeklyOffBreakdown[dayName] || 0) + 1;
                }
            }

            console.log('🔢 Weekly Off Breakdown:', weeklyOffBreakdown, 'Total:', nonWorkingDays);

            // Get holidays from holiday plan (HD)
            let holidayDays = 0;
            const holidayPlanIdToUse = holidayPlanId || userProfile?.holiday_plan_id || userProfile?.holidayPlanId;
            const holidaysList = [];

            if (holidayPlanIdToUse) {
                try {
                    const holidayPlan = await holidayPlanService.getHolidayPlanById(holidayPlanIdToUse);
                    const holidays = holidayPlan?.holidays || [];

                    console.log(`🎉 Holiday Plan (ID: ${holidayPlanIdToUse}):`, holidayPlan?.name || 'Unknown', '- Total holidays:', holidays.length);

                    // Count holidays in current month (excluding those that fall on weekly offs)
                    holidays.forEach(holiday => {
                        const holidayDate = new Date(holiday.date);
                        if (holidayDate.getFullYear() === year &&
                            holidayDate.getMonth() === month - 1) {
                            const dayOfWeek = holidayDate.getDay();
                            const isWeeklyOff = weeklyOffDays.includes(dayOfWeek);

                            // Only count if it's not already a weekly off
                            if (!isWeeklyOff) {
                                holidayDays++;
                                holidaysList.push({
                                    name: holiday.name,
                                    date: holiday.date,
                                    day: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][dayOfWeek]
                                });
                            } else {
                                console.log(`  ⚠️ Holiday "${holiday.name}" on ${holiday.date} falls on weekly off, not counted`);
                            }
                        }
                    });

                    if (holidaysList.length > 0) {
                        console.log('🎉 Holidays counted in this month:', holidaysList);
                    } else {
                        console.log('🎉 No holidays (or all fall on weekly offs) in this month');
                    }
                } catch (error) {
                    console.warn('⚠️ Could not fetch holiday plan:', error);
                }
            } else {
                console.log('🎉 No holiday plan assigned to user');
            }

            // Calculate working days: WD = TD - ND - HD
            const workingDays = totalDaysInMonth - nonWorkingDays - holidayDays;

            console.log('📅 Working Days Calculation:', {
                month: `${year}-${month}`,
                totalDaysInMonth: `TD = ${totalDaysInMonth}`,
                nonWorkingDays: `ND = ${nonWorkingDays}`,
                holidayDays: `HD = ${holidayDays}`,
                workingDays: `WD = ${workingDays}`,
                formula: `WD = TD - ND - HD = ${totalDaysInMonth} - ${nonWorkingDays} - ${holidayDays} = ${workingDays}`,
                weeklyOff: userProfile?.weekly_off || userProfile?.weeklyOff,
                weeklyOffDaysCount: weeklyOffDays.length,
                holidayPlanId: holidayPlanIdToUse
            });

            return {
                totalDaysInMonth,
                nonWorkingDays,
                holidayDays,
                workingDays
            };
        } catch (error) {
            console.error('Error calculating working days:', error);
            const totalDaysInMonth = new Date(year, month, 0).getDate();
            return {
                totalDaysInMonth,
                nonWorkingDays: 0,
                holidayDays: 0,
                workingDays: totalDaysInMonth
            };
        }
    }

    /**
     * Get monthly attendance summary for current month
     * Calculates from actual attendance data
     */
    async getMonthlyAttendanceSummary(userProfile = null) {
        try {
            const now = new Date();
            const month = now.getMonth() + 1;
            const year = now.getFullYear();

            // Get actual attendance data for the month
            const attendanceData = await this.getMyAttendance(month, year);

            // Backend calculates all stats correctly, trust its values
            if (attendanceData?.stats) {
                console.log('✅ Dashboard using backend-calculated attendance stats:', {
                    totalWorkingDays: attendanceData.stats.totalDays,
                    presentDays: attendanceData.stats.presentDays,
                    totalDaysInMonth: attendanceData.stats.totalDaysMonth || new Date(year, month, 0).getDate()
                });

                return {
                    presentDays: attendanceData.stats.presentDays || 0,
                    totalWorkingDays: attendanceData.stats.totalDays || 0,
                    totalDaysInMonth: attendanceData.stats.totalDaysMonth || new Date(year, month, 0).getDate()
                };
            }

            // Fallback: Backend didn't provide working days, calculate on frontend
            console.log('⚠️ Backend did not provide working days, calculating on frontend for dashboard...');

            // Count present days from monthly attendance records
            let presentDays = 0;
            const monthlyRecords = attendanceData?.monthly || [];

            monthlyRecords.forEach(record => {
                // Count as present if status is 'present' or if there's a check-in time
                if (record.status === 'present' ||
                    record.status === 'Present' ||
                    record.check_in_time ||
                    record.attendance_status === 'present' ||
                    record.attendance_status === 'Present') {
                    presentDays++;
                }
            });

            // If userProfile provided, calculate working days properly
            let workingDaysData;
            if (userProfile) {
                // Check if user profile has the necessary fields
                const hasWeeklyOff = userProfile.weekly_off || userProfile.weeklyOff;
                const hasHolidayPlan = userProfile.holiday_plan_id || userProfile.holidayPlanId;

                // If essential fields are missing, try to fetch full profile
                if (!hasWeeklyOff || !hasHolidayPlan) {
                    console.warn('⚠️ User profile missing weekly_off or holiday_plan_id, fetching full profile...');
                    try {
                        const { default: employeeService } = await import('./employee.service');
                        const profileResponse = await employeeService.getProfile();
                        if (profileResponse) {
                            userProfile = { ...userProfile, ...profileResponse };
                            console.log('✅ Full profile fetched:', {
                                weeklyOff: userProfile.weekly_off,
                                holidayPlanId: userProfile.holiday_plan_id
                            });
                        }
                    } catch (error) {
                        console.error('❌ Error fetching full profile:', error);
                    }
                }

                console.log('👤 User Profile for Working Days:', {
                    userId: userProfile.id,
                    weeklyOff: userProfile.weekly_off || userProfile.weeklyOff,
                    holidayPlanId: userProfile.holiday_plan_id || userProfile.holidayPlanId
                });

                workingDaysData = await this.calculateWorkingDays(
                    year,
                    month,
                    userProfile,
                    userProfile.holiday_plan_id || userProfile.holidayPlanId
                );
            } else {
                console.warn('⚠️ No user profile provided, using fallback calculation');
                // Fallback: Get from stats or estimate
                const totalDaysInMonth = new Date(year, month, 0).getDate();
                let totalWorkingDays = attendanceData?.stats?.totalWorkingDays || 0;

                // If stats not available, use simple calculation (exclude Sundays)
                if (!totalWorkingDays) {
                    let workingDaysCount = 0;
                    for (let day = 1; day <= totalDaysInMonth; day++) {
                        const date = new Date(year, month - 1, day);
                        const dayOfWeek = date.getDay();
                        if (dayOfWeek !== 0) { // Not Sunday
                            workingDaysCount++;
                        }
                    }
                    totalWorkingDays = workingDaysCount;
                }

                workingDaysData = {
                    totalDaysInMonth,
                    workingDays: totalWorkingDays
                };
            }

            return {
                presentDays,
                totalWorkingDays: workingDaysData.workingDays,
                totalDaysInMonth: workingDaysData.totalDaysInMonth
            };
        } catch (error) {
            console.error('Error fetching monthly attendance summary:', error);
            const now = new Date();
            const month = now.getMonth() + 1;
            const year = now.getFullYear();
            const totalDaysInMonth = new Date(year, month, 0).getDate();

            return {
                presentDays: 0,
                totalWorkingDays: 0,
                totalDaysInMonth
            };
        }
    }

    // ============================================================
    // ACTIVITY LOGGING (Anti-Misuse Tracking)
    // ============================================================

    /**
     * Log activity silently (called from frontend without user seeing it)
     * Used for: app_load, page_view
     * @param {string} eventType - 'app_load', 'page_view', 'punch_attempt', 'regularization_submit'
     * @param {object} options - Additional options (targetDate, deviceType, metadata)
     */
    async logActivity(eventType, options = {}) {
        try {
            const payload = {
                eventType,
                targetDate: options.targetDate || null,
                punchType: options.punchType || null,
                success: options.success !== undefined ? options.success : null,
                failureReason: options.failureReason || null,
                deviceType: options.deviceType || 'web',
                metadata: options.metadata || null
            };

            // Fire and forget - don't await, don't throw
            api.post('/api/attendance/log-activity', payload).catch(() => {
                // Silently ignore errors
            });
        } catch {
            // Silently ignore errors - activity logging should never break the app
        }
    }

    /**
     * Get activity logs for an employee (Admin/Manager only)
     * @param {string} employeeId - Employee UUID
     * @param {object} filters - Filter options (fromDate, toDate, eventType, success)
     */
    async getActivityLogs(employeeId, filters = {}) {
        try {
            const params = {};
            if (filters.fromDate) params.fromDate = filters.fromDate;
            if (filters.toDate) params.toDate = filters.toDate;
            if (filters.eventType) params.eventType = filters.eventType;
            if (filters.success !== undefined) params.success = filters.success;

            const response = await api.get(`/api/attendance/activity-log/${employeeId}`, { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching activity logs:', error);
            throw error;
        }
    }

    /**
     * Get activity summary for a specific date (for regularization review)
     * @param {string} employeeId - Employee UUID
     * @param {string} date - Date in YYYY-MM-DD format
     */
    async getActivitySummary(employeeId, date) {
        try {
            const response = await api.get(`/api/attendance/activity-summary/${employeeId}/${date}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching activity summary:', error);
            throw error;
        }
    }
}

export default new AttendanceService();
